import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { useHref } from "../../../packages/router/href/HrefProvider.js";
import { CloudEntry } from "../../model/cloud/CloudEntry.js";
import { Progress, Uploader } from "../../../packages/fetcher/fetcherFunctions.js";
import { generateUuid } from "../../../generate-uuid/generateUuid.js";
import { getSortedEntries } from "../../model/cloud/getSortedEntries.js";
import { createNewDirectory, deletePath, getDirectoryEntries, uploadDirectoryFiles } from "./cloudApi.js";
import { validateDirectoryName } from "../../model/cloud/validateDirectoryName.js";
import { useLoadEffect } from "../../../react/load/useLoadEffect.js";

type CloudState = {
  directory: string;
  entries: CloudEntry[];
  filesInputKey: string;
  removePath: (path: string) => void;
  uploadFiles: (files: FileList) => void;
  cancelUpload: () => void;
  uploadError?: string;
  isUploading: boolean;
  progress?: Progress;
  directoryError?: string;
  createDirectory: () => void;
  directoryName: string;
  setDirectoryName: (name: string) => void;
};

const CloudContext = createContext<CloudState | undefined>(undefined);

type Props = {
  children: ReactNode;
};

/**
 * Cloud view state provider.
 */
export const CloudProvider = ({ children }: Props) => {
  const { href } = useHref();

  const [isUploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>();

  const [progress, setProgress] = useState<Progress>();
  const [uploader, setUploader] = useState<Uploader>();

  const [filesInputKey, setFilesInputKey] = useState(() => generateUuid());

  const [directoryError, setDirectoryError] = useState<string>();
  const [directoryName, setDirectoryName] = useState<string>("");

  const directory = useMemo(() => {
    const searchParams = new URL(href).searchParams;
    return searchParams.get("directory") ?? "/";
  }, [href]);

  const result = useLoadEffect(`cloud-${directory}`, async () =>
    getSortedEntries(await getDirectoryEntries(directory))
  );

  const entries = useMemo(() => (result.state === "success" ? result.data : []), [result]);

  const removePath = useCallback(
    async (path: string) => {
      if (confirm(`Remove entry ${path}?`)) {
        await deletePath(path);
        result.reload();
      }
    },
    [result]
  );

  const uploadFiles = useCallback(
    async (files: FileList) => {
      const data = new FormData();
      for (let i = 0; i < files.length; i++) {
        data.append("files", files[i]);
      }
      setUploadError("");
      setUploading(true);
      setProgress(undefined);
      try {
        await uploadDirectoryFiles(directory, data, setUploader, setProgress);
      } catch (err) {
        setUploadError(err + "");
      } finally {
        setUploading(false);
        result.reload();
        setFilesInputKey(generateUuid());
        setProgress(undefined);
      }
    },
    [directory, result]
  );

  const cancelUpload = useCallback(() => {
    if (uploader) {
      uploader.cancel();
    }
  }, [uploader]);

  const createDirectory = useCallback(async () => {
    setDirectoryError(undefined);

    const trimmedName = directoryName.trim();

    if (trimmedName === "") {
      setDirectoryError("Please enter directory name.");
      return;
    }

    if (entries.some((entry) => entry.name === trimmedName)) {
      setDirectoryError("Entry already exists.");
      return;
    }

    try {
      validateDirectoryName(trimmedName);
      await createNewDirectory(directory, trimmedName);
      result.reload();
      setDirectoryName("");
    } catch (err) {
      setDirectoryError(err + "");
    }
  }, [directory, directoryName, entries, result]);

  const value: CloudState = {
    directory,
    entries,
    uploadFiles,
    removePath,
    filesInputKey,
    cancelUpload,
    uploadError,
    isUploading,
    progress,
    directoryError,
    createDirectory,
    directoryName,
    setDirectoryName
  };

  return <CloudContext.Provider value={value}>{children}</CloudContext.Provider>;
};

/**
 * Helper hook to access the cloud view state.
 */
export const useCloud = () => {
  const context = useContext(CloudContext);
  if (!context) {
    throw new Error("The cloud context is not provided.");
  }
  return context;
};
